import { MainUtils, useMountedState } from '@bimser/common';
import {
	BCButton,
	BCButtonOverflow,
	FlexPanelActionTypes,
	IButtonOverflowItem,
	findMessage
} from '@bimser/components';
import { RxEventEmitter } from '@bimser/core';
import IconCloseWindow from '@bimser/icons/16/close-window';
import IconMaximizeWindow from '@bimser/icons/16/maximize-window';
import classNames from 'classnames/bind';
import React, { JSX } from 'react';
import { Subscription } from 'rxjs';
import components from '../../../panelComponents';
import Style from '../assets/style.scss';
import IPanelComponentProps from '../entities/IPanelComponentProps';

const cx = classNames.bind(Style);
const overflowButtonProps = { type: 'text' as const };

const usePanelComponent = (props: IPanelComponentProps) => {
	const [rxSubscription, setRxSubscription] = React.useState<Subscription>(null);
	const [ribbonProps, setRibbonProps] = React.useState<{ items: IButtonOverflowItem[] }>(null);
	const [customHeader, setCustomHeader] = React.useState<JSX.Element>(null);
	const [title, setTitle] = React.useState(props.title);
	const [canFullScreen, setCanFullScreen] = React.useState(false);

	const didMount = useMountedState();
	const componentWidth = props.subProps?.viewerPayload?.data?.width;
	const isFullScreen = props.subProps?.viewerPayload?.data?.isFullScreen;
	const iconFullScrenTitle = canFullScreen
		? findMessage.get('101578')
		: findMessage.get('101577');

	React.useEffect(() => {
		if (didMount) {
			RxEventEmitter.listen(props.modalId, {
				next: rxEventHandler
			}).then(_rxSubscription => {
				setRxSubscription(_rxSubscription);
			});
		}
		return () => {
			if (rxSubscription) {
				rxSubscription.unsubscribe();
			}
		};
	}, []);

	const toggleFullScreen = () => {
		setCanFullScreen(!canFullScreen);
	};

	const rxEventHandler = React.useCallback((event: any) => {
		if (event.type == FlexPanelActionTypes.SET_HEADER_EVENTS) {
			setRibbonProps(event.data);
		} else if (event.type == FlexPanelActionTypes.SET_PANEL_TITLE) {
			setTitle(event.data);
		} else if (event.type == FlexPanelActionTypes.SET_CUSTOM_HEADER) {
			setCustomHeader(event.data);
		}
	}, []);

	const onCancelClick = React.useCallback(() => {
		props.onCancel(props.modalId, props.panelType);
	}, [setRibbonProps, setTitle, setCustomHeader]);

	const Component = React.useMemo(() => {
		const componentKey = MainUtils.GetComponentKeyFromPanelType(props.panelType);
		return components[componentKey];
	}, []);

	const renderContent = React.useCallback(() => {
		const _props = {
			panelGuid: props.modalId,
			parentPanel: props.parentPanel,
			panelType: props.panelType,
			actionRole: props.actionRole,
			isPanelActive: true,
			windowType: props.type
		};
		return (
			<Component
				{...props.subProps}
				{..._props}
			/>
		);
	}, []);

	const renderTitle = (_title: string) => {
		if (customHeader) {
			return customHeader;
		}
		return (
			<div className={Style.title}>
				<span title={_title}>{_title}</span>
			</div>
		);
	};

	const renderCustomEvents = () => {
		const hasItems = ribbonProps?.items?.length;

		return (
			<div className={cx({ events: true, hasItems })}>
				{hasItems ? (
					<div className={cx({ eventButtons: true, divider: true })}>
						<BCButtonOverflow
							items={ribbonProps.items}
							overflowButtonProps={overflowButtonProps}
							alignRight
						/>
					</div>
				) : null}
				{renderPanelHeaderActions()}
			</div>
		);
	};

	const renderCloseButton = () => {
		return (
			<BCButton
				title={findMessage.get('100824')}
				onClick={onCancelClick}
				icon={<IconCloseWindow />}
				type="text"
			/>
		);
	};

	const renderFullScreenButton = () => {
		return (
			<BCButton
				title={iconFullScrenTitle}
				onClick={toggleFullScreen}
				icon={<IconMaximizeWindow />}
				type="text"
			/>
		);
	};

	const renderPanelHeaderActions = () => {
		return (
			<div className={Style.actions}>
				{isFullScreen ? renderFullScreenButton() : null}
				{renderCloseButton()}
			</div>
		);
	};

	const renderHeader = React.useCallback(
		(_title: string) => {
			return (
				<div
					className={cx({
						header: true
					})}
				>
					{renderTitle(_title)}
					{renderCustomEvents()}
				</div>
			);
		},
		[customHeader, ribbonProps, onCancelClick, componentWidth, canFullScreen]
	);

	return {
		renderContent,
		renderHeader,
		title,
		onCancelClick,
		componentWidth,
		canFullScreen
	};
};
export default usePanelComponent;
